import { useEffect, useState } from 'react'
import ClearIcon from '@mui/icons-material/Clear'
import PlanChangePaymentForm from '../../../../payment/PlanChangePaymentForm'
import CustomButton from '../../../../ui/CustomButton'
import { Modal } from '../../../../ui/Modal'
import PaymentCardSelector from './PaymentCardSelector'

const getPlanFrequencyLabel = frequency => {
  if (frequency === 'yearly' || frequency === 'annual') return 'Anual'
  if (frequency === 'monthly') return 'Mensual'
  return 'Plan'
}

const PlanChangeConfirmContent = ({
  selectedPlan,
  ufValue,
  changePlanStep,
  onPaymentSuccess,
  onPaymentLayoutChange,
  isPending,
  customerId,
  cardsIds,
  selectedCard,
  cardsLoading = false,
  cards = [],
  selectedCardId = '',
  onSelectCard,
  cardSelectionStepSkipped = false,
  isPendingNewCardConfirmation = false,
  errorMessage,
  hasCurrentPlan = true,
  paymentTypeId = null
}) => {
  const [showFinalConfirmationModal, setShowFinalConfirmationModal] = useState(false)
  const actionLabel = hasCurrentPlan ? 'cambio de plan' : 'contratacion del plan'
  const planPrice =
    selectedPlan?.ufPriceWithoutTaxes != null ? Number(selectedPlan.ufPriceWithoutTaxes) : null
  const hasUpgradeDiscount = Boolean(
    selectedPlan?.upgradeDiscountUf && selectedPlan.upgradeDiscountUf > 0
  )
  const discountedPrice =
    hasUpgradeDiscount && planPrice != null
      ? planPrice - Number(selectedPlan.upgradeDiscountUf)
      : null
  const amount = planPrice && ufValue ? Math.round(planPrice * ufValue * 1.19) : 1
  const isFinalConfirmationStep = changePlanStep === 2
  const planFrequencyLabel = getPlanFrequencyLabel(selectedPlan?.frequency)
  const firstChargeUf = discountedPrice ?? planPrice
  const finalSubmitLabel = hasCurrentPlan ? 'Activar cambio y pagar' : 'Activar plan y pagar'
  const finalConfirmationFormId = 'plan-final-confirmation-form'
  const canSelectCardInCurrentStep =
    !isFinalConfirmationStep && !isPendingNewCardConfirmation && cards.length > 1

  useEffect(() => {
    setShowFinalConfirmationModal(isFinalConfirmationStep)
  }, [isFinalConfirmationStep])

  const paymentForm = (
    <PlanChangePaymentForm
      key={`${changePlanStep}-${selectedCard?.id ?? 'none'}-${paymentTypeId ?? 'all'}`}
      amount={amount}
      onSuccess={onPaymentSuccess}
      customerId={customerId}
      cardsIds={cardsIds}
      showSavedCard={isFinalConfirmationStep}
      savedCardCvvOnly={isFinalConfirmationStep}
      formId={isFinalConfirmationStep ? finalConfirmationFormId : undefined}
      hideSubmitButton={isFinalConfirmationStep}
      brickId={changePlanStep === 1 ? 'planChange' : 'planChangeConfirm'}
      submitLabel={isFinalConfirmationStep ? finalSubmitLabel : undefined}
      paymentTypeId={paymentTypeId}
      securityMessage={
        isFinalConfirmationStep
          ? undefined
          : 'Por seguridad, te pediremos ingresar nuevamente los datos de la misma tarjeta inscrita en el paso anterior. En este paso realizaremos el cobro del plan seleccionado.'
      }
      onLayoutChange={onPaymentLayoutChange}
    />
  )

  return (
    <div className='relative space-y-6'>
      <Modal
        isOpen={showFinalConfirmationModal && isFinalConfirmationStep}
        onClose={() => setShowFinalConfirmationModal(false)}
        closeOnBackdrop={false}
      >
        <div className='flex max-h-[92vh] w-[min(44rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl'>
          <div className='flex items-center justify-between bg-primary px-4 py-2 text-white sm:px-8'>
          <div>
            <p className='text-[11px] font-semibold uppercase tracking-[0.22em] text-white/70'></p>
            <h2 className='mt-1 text-2xl font-bold'></h2>
          </div>

            <button
              type='button'
              onClick={() => setShowFinalConfirmationModal(false)}
              className='modal-control text-white hover:bg-white/20 rounded p-0.5 transition-colors'
              aria-label='Cerrar confirmacion final'
            >
              <ClearIcon style={{ fontSize: 20 }} />
            </button>
          </div>

          <div className='flex-1 space-y-5 overflow-y-auto px-6 py-6 sm:px-8'>
            <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
              <div className='flex flex-col gap-4'>
                <div className='flex items-start justify-between gap-4'>
                <div>
                  <p className='text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-slate-500'> Plan seleccionado</p>
                  <div className='mt-2 flex items-center gap-2'>
                    <p className='text-sm font-sans font-bold text-slate-800'> {selectedPlan?.name || 'Plan'} </p>
                    <p className='text-sm font-sans text-slate-500'> {planFrequencyLabel} </p>
                  </div>
                </div>
              </div>

                <div className='rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-4'>
                  {planPrice != null && (
                    <div className='flex items-center justify-between gap-4 text-sm font-sans text-slate-700'>
                      <span>Precio</span>
                      <span className='font-semibold text-slate-900'>
                        {planPrice.toFixed(2)} UF + IVA
                      </span>
                    </div>
                  )}

                  {firstChargeUf != null && (
                    <div className='mt-3 flex items-center justify-between gap-4 border-t border-slate-200 pt-3'>
                      <div>
                        <p className='text-sm font-sans font-semibold text-teal-700'>
                          Primer cobro hoy
                        </p>
                        {hasUpgradeDiscount && (
                          <p className='mt-1 text-xs font-sans text-teal-600'>
                            Incluye descuento aplicado
                          </p>
                        )}
                      </div>
                      <span className='text-base font-sans font-bold text-teal-700'>
                        {firstChargeUf.toFixed(2)} UF + IVA
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {selectedCard ? (
              <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
                <div>
                  <p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                    Esta sera la tarjeta con la que se cobrara y validara tu plan.
                  </p>
                </div>

                <div className='mt-4 pointer-events-none'>
                  <PaymentCardSelector
                    cards={[selectedCard]}
                    selectedCardId={String(selectedCard.id)}
                  />
                </div>
              </div>
            ) : (
              <div className='rounded-[24px] border border-amber-200 bg-amber-50 p-4 text-sm font-sans text-amber-800 shadow-sm'>
                No se encontro la tarjeta seleccionada. Vuelve al paso anterior y elige una tarjeta
                antes de confirmar.
              </div>
            )}

            <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
              <div className='mb-4'>
                <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.18em] text-primary/80'>
                  Ingresa tu CVV para confirmar
                </p>
                <p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                  Por seguridad, te pedimos este codigo para confirmar la transaccion. Tus datos
                  estan protegidos y se procesaran de forma segura.
                </p>
              </div>

              {errorMessage && (
                <div className='mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-sans text-red-700'>
                  {errorMessage}
                </div>
              )}

              {paymentForm}
            </div>
          </div>

          <div className='flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:px-8'>
            <CustomButton
              title='Volver'
              color='primary'
              className='rounded-full px-5 py-2'
              onClick={() => setShowFinalConfirmationModal(false)}
            />
            <CustomButton
              type='submit'
              form={finalConfirmationFormId}
              title={finalSubmitLabel}
              color='accent2'
              className='rounded-full px-5 py-2'
              disabled={isPending}
            />
          </div>
        </div>
      </Modal>

      {isPending && (
        <div className='absolute inset-0 z-50 flex flex-col items-center justify-center rounded-lg bg-white/90'>
          <div className='mb-4 h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-accent1' />
          <h3 className='mb-2 text-xl font-bold text-gray-900'>Procesando {actionLabel}</h3>
          <p className='text-center text-gray-600'>
            {changePlanStep === 1 ? 'Creando suscripcion...' : 'Confirmando pago...'}
          </p>
        </div>
      )}

      <div className='grid gap-6 py-4 xl:grid-cols-[minmax(320px,0.9fr)_minmax(0,1.3fr)]'>
        <div className='space-y-6'>
          <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
            <div className='max-w-xl'>
              <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.18em] text-primary/80'>
                Confirmacion
              </p>
              <h4 className='mt-2 text-2xl font-sans font-bold text-slate-800'>
                {hasCurrentPlan
                  ? 'Revisa el cambio antes de autorizar el cobro'
                  : 'Confirma tu tarjeta para activar tu plan'}
              </h4>
              <ul className='mt-3 list-disc space-y-2 pl-5 text-sm font-sans leading-6 text-slate-600'>
                <li>
                  {hasCurrentPlan
                    ? 'Estas a punto de cambiar al plan '
                    : 'Estas a punto de contratar el plan '}
                  <strong>{selectedPlan?.name || 'seleccionado'}</strong>.
                </li>
                <li>
                  {canSelectCardInCurrentStep
                    ? 'El cobro se realizara inmediatamente sobre la tarjeta que selecciones a continuacion y confirmaremos en este paso.'
                    : cardSelectionStepSkipped
                      ? 'El cobro se realizara inmediatamente sobre tu tarjeta guardada y confirmaremos en este paso.'
                      : 'El cobro se realizara inmediatamente sobre la tarjeta que elegiste en el paso anterior y confirmaremos en este paso.'}
                </li>
                <li>Tus datos estan protegidos y seran procesados de forma segura.</li>
              </ul>
            </div>
          </div>

          <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
            <div>
              <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.16em] text-primary/80'>
                Plan seleccionado
              </p>
              <p className='mt-1 text-lg font-sans font-bold text-slate-800'>
                {selectedPlan?.name || 'Plan'}
              </p>
            </div>

            {planPrice != null && (
              <div className='mt-4 rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-4'>
                <p className='text-sm font-sans text-slate-600'>Precio del plan</p>
                <p className='mt-1 text-base font-sans font-semibold text-slate-800'>
                  {planPrice.toFixed(2)} UF + IVA
                </p>
              </div>
            )}

            {hasUpgradeDiscount && discountedPrice != null && (
              <div className='mt-4 rounded-[20px] border border-green-200 bg-green-50 p-4'>
                <div className='mb-2 flex items-center gap-2'>
                  <span className='text-sm font-sans font-semibold text-green-700'>
                    Descuento por adelanto aplicado
                  </span>
                </div>
                <div className='space-y-1 text-xs font-sans text-green-800'>
                  <div className='flex justify-between gap-4'>
                    <span>Descuento</span>
                    <span className='font-semibold'>
                      -{Number(selectedPlan.upgradeDiscountUf).toFixed(2)} UF
                    </span>
                  </div>
                  <div className='flex justify-between gap-4 border-t border-green-200 pt-1'>
                    <span className='font-medium'>Primer pago</span>
                    <span className='font-bold'>{discountedPrice.toFixed(2)} UF + IVA</span>
                  </div>
                  <p className='mt-2 text-xs italic text-green-700'>
                    Este descuento se aplica solo al primer pago por el adelanto ya pagado en tu
                    plan actual.
                  </p>
                </div>
              </div>
            )}
          </div>

          {selectedCard ? (
            <div className='space-y-4'>
              {isPendingNewCardConfirmation && (
                <div className='rounded-[24px] border border-blue-200 bg-blue-50 p-4 text-sm font-sans text-blue-800 shadow-sm'>
                  La tarjeta nueva ya fue registrada y se usara en esta confirmacion final. Su
                  informacion se mostrara en la lista cuando completes el proceso.
                </div>
              )}

              <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
                <div>
                  <p className='text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-slate-500'>
                    Tarjeta seleccionada
                  </p>
                  <p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                    Esta es la tarjeta que se usara para completar la autorizacion del plan.
                  </p>
                </div>

                <div className='mt-4 pointer-events-none'>
                  <PaymentCardSelector
                    cards={[selectedCard]}
                    selectedCardId={String(selectedCard.id)}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className='rounded-[24px] border border-amber-200 bg-amber-50 p-4 text-sm font-sans text-amber-800 shadow-sm'>
              No se encontro la tarjeta seleccionada. Vuelve al paso anterior y elige una tarjeta
              antes de confirmar.
            </div>
          )}
        </div>

        <div className='min-w-0'>
          <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
            {isFinalConfirmationStep ? (
              <div className='space-y-4'>
                <div>
                  <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.18em] text-primary/80'>
                    Confirmacion final
                  </p>
                  <h4 className='mt-2 text-xl font-sans font-bold text-slate-800'>
                    El ingreso del CVV
                  </h4>
                  <p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                    Abrimos automaticamente la ventana de confirmacion para que completes el CVV y
                    autorices el cobro del plan. Si la cerraste, puedes abrirla nuevamente desde
                    aqui.
                  </p>
                </div>

                {errorMessage && (
                  <div className='rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-sans text-red-700'>
                    {errorMessage}
                  </div>
                )}

                <CustomButton
                  title='Abrir confirmacion final'
                  color='accent2'
                  className='rounded-full px-5 py-2'
                  onClick={() => setShowFinalConfirmationModal(true)}
                />
              </div>
            ) : (
              <>
                {canSelectCardInCurrentStep && (
                  <div className='mb-6 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm'>
                    <div className='mb-4'>
                      <p className='text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-slate-500'>
                        Tarjeta para el cobro
                      </p>
                      <p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                        Elige la tarjeta guardada con la que se procesara este plan.
                      </p>
                    </div>

                    {cardsLoading ? (
                      <div className='flex items-center justify-center rounded-[24px] border border-slate-200 bg-white py-10 shadow-sm'>
                        <div className='h-12 w-12 animate-spin rounded-full border-b-4 border-t-4 border-accent1' />
                      </div>
                    ) : (
                      <PaymentCardSelector
                        cards={cards}
                        selectedCardId={selectedCardId}
                        onSelectCard={onSelectCard}
                        disabled={isPending}
                      />
                    )}
                  </div>
                )}

                <div className='mb-4'>
                  <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.18em] text-primary/80'>
                    Validacion
                  </p>
                  {/*<p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                    Por seguridad, te pediremos ingresar nuevamente los datos de la misma tarjeta
                    inscrita en el paso anterior. En este paso realizaremos el cobro del plan
                    seleccionado.
                  </p>*/}
                </div>

                {errorMessage && (
                  <div className='mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-sans text-red-700'>
                    {errorMessage}
                  </div>
                )}

                {paymentForm}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlanChangeConfirmContent
